import { create } from "zustand";
import EasySpeech from "easy-speech";

import i18n from "@/i18n";
import { logger } from "@/telemetry";

interface Voice {
  name: string;
  lang: string;
}

export interface SpeechStoreState {
  voices: Voice[];
  selectedVoice: string;
  rate: number;
  pitch: number;
  speakingMessageId: string | null;
  setSelectedVoice: (voice: string) => void;
  speak: (text: string, messageId: string) => void;
  stopSpeech: () => void;
}

// Initialize easy-speech
const initEasySpeech = async () => {
  try {
    await EasySpeech.init({ maxTimeout: 15000, interval: 250 });
  } catch (error) {
    logger.warn("Failed to initialize EasySpeech", { error: String(error) });
  }
};

// Ensure EasySpeech is initialized before getting voices
const getVoices = async () => {
  try {
    let voices = await EasySpeech.voices();

    // Edge may populate voices asynchronously; wait briefly if empty
    if (!voices || voices.length === 0) {
      const start = Date.now();
      const maxWaitMs = 5000;

      await new Promise<void>((resolve) => {
        let resolved = false;
        const tryLoad = async () => {
          const v = await EasySpeech.voices();

          if (v && v.length > 0) {
            voices = v;
            if (!resolved) {
              resolved = true;
              resolve();
            }
          } else if (Date.now() - start >= maxWaitMs) {
            if (!resolved) {
              resolved = true;
              resolve();
            }
          } else {
            setTimeout(tryLoad, 250);
          }
        };

        tryLoad();
        if (typeof window !== "undefined" && window.speechSynthesis) {
          const handler = () => {
            tryLoad();
          };

          try {
            window.speechSynthesis.addEventListener("voiceschanged", handler, {
              once: true,
            } as any);
          } catch {
            // Ignore errors when requesting permission
          }
        }
      });
    }

    // Map voices to our format
    let formattedVoices = voices.map((voice) => ({
      name: voice.name,
      lang: voice.lang,
    }));

    const supported = ["en", "de", "es", "fr", "it", "pt"];
    const picks: { name: string; lang: string }[] = [];

    for (const base of supported) {
      const perLang = formattedVoices.filter((v) => (v.lang || "").toLowerCase().startsWith(base));

      if (perLang.length === 0) continue;
      const google = perLang.find((v) => (v.name || "").toLowerCase().includes("google"));
      const microsoft = perLang.find((v) => (v.name || "").toLowerCase().includes("microsoft"));
      const eddy = perLang.find((v) => (v.name || "").toLowerCase().includes("daniel"));

      if (google) picks.push(google);
      else if (microsoft) picks.push(microsoft);
      else if (eddy) picks.push(eddy);
    }

    if (picks.length > 0) {
      formattedVoices = picks;
    } else {
      // Fallback: if no Google/Microsoft for any supported language, try any Eddy
      const appLang = (i18n.language || "en-GB").toLowerCase();
      const base = appLang.split("-")[0];
      const baseEddy = formattedVoices.find(
        (v) =>
          (v.name || "").toLowerCase().includes("eddy") &&
          (v.lang || "").toLowerCase().startsWith(base),
      );

      if (baseEddy) {
        formattedVoices = [baseEddy];
      } else {
        const anyGoogle = formattedVoices.find((v) =>
          (v.name || "").toLowerCase().includes("google"),
        );
        const anyMicrosoft = formattedVoices.find((v) =>
          (v.name || "").toLowerCase().includes("microsoft"),
        );
        const anyEddy = formattedVoices.find((v) =>
          (v.name || "").toLowerCase().includes("daniel"),
        );

        if (anyGoogle || anyMicrosoft || anyEddy) {
          formattedVoices = [
            anyGoogle || anyMicrosoft || anyEddy || { name: "No Voice", lang: "" },
          ];
        } else {
          // Keep current mapped list as last resort
          // (will be expanded below to full list if nothing else is available)
          // Keep current mapped list as is
        }
      }
    }

    // Add a "No Voice" option at the beginning (and ensure at least one option exists)
    formattedVoices.unshift({ name: "No Voice", lang: "" });
    if (formattedVoices.length === 1) {
      // No preferred voice found; keep whatever the browser has as a fallback list
      formattedVoices = [
        { name: "No Voice", lang: "" },
        ...voices.map((v) => ({ name: v.name, lang: v.lang })),
      ];
    }

    return formattedVoices;
  } catch (error) {
    logger.warn("Error getting voices", { error: String(error) });

    return [{ name: "No Voice", lang: "" }];
  }
};

const useSpeechStore = create<SpeechStoreState>((set, get) => {
  // Initialize with default values
  const initialState = {
    voices: [{ name: "No Voice", lang: "" }],
    selectedVoice: localStorage.getItem("selectedVoice") || "No Voice",
    rate: 1,
    pitch: 1,
    speakingMessageId: null,
  };

  // Initialize EasySpeech and load voices
  (async () => {
    try {
      await initEasySpeech();
      const voices = await getVoices();

      // Only update state if initialization was successful
      set(() => ({ voices }));

      // Auto-select a default voice that matches the platform language if possible
      try {
        const currentSelected = get().selectedVoice;
        const hasCurrent = voices.some((v) => v.name === currentSelected);
        const appLang = (i18n.language || "en-GB").toLowerCase();
        const base = appLang.split("-")[0];
        const match =
          voices.find((v) => (v.lang || "").toLowerCase() === appLang) ||
          voices.find((v) => (v.lang || "").toLowerCase().startsWith(base)) ||
          null;

        if ((!hasCurrent || currentSelected === "No Voice") && match) {
          localStorage.setItem("selectedVoice", match.name);
          set(() => ({ selectedVoice: match.name }));
        } else if (!hasCurrent && voices.length > 1) {
          const fallback = voices[1].name;

          localStorage.setItem("selectedVoice", fallback);
          set(() => ({ selectedVoice: fallback }));
        }
      } catch (e) {
        logger.info("Failed to auto-select default voice", { error: String(e) });
      }

      // Additional initialization for mobile devices
      if (typeof window !== "undefined" && window.speechSynthesis) {
        // Some mobile browsers need an initial interaction with speechSynthesis
        // to properly initialize it
        try {
          window.speechSynthesis.cancel();
        } catch (e) {
          logger.info("Initial speechSynthesis interaction error (can be ignored)", {
            error: String(e),
          });
        }
        try {
          window.speechSynthesis.addEventListener(
            "voiceschanged",
            async () => {
              const updated = await getVoices();

              set(() => ({ voices: updated }));
              const currentSelected = get().selectedVoice;
              const hasCurrent = updated.some((v) => v.name === currentSelected);
              const appLang = (i18n.language || "en-GB").toLowerCase();
              const base = appLang.split("-")[0];
              const match =
                updated.find((v) => (v.lang || "").toLowerCase() === appLang) ||
                updated.find((v) => (v.lang || "").toLowerCase().startsWith(base)) ||
                null;

              if ((!hasCurrent || currentSelected === "No Voice") && match) {
                localStorage.setItem("selectedVoice", match.name);
                set(() => ({ selectedVoice: match.name }));
              } else if (!hasCurrent && updated.length > 1) {
                const fallback = updated[1].name;

                localStorage.setItem("selectedVoice", fallback);
                set(() => ({ selectedVoice: fallback }));
              }
            },
            { once: true } as any,
          );
        } catch {
          // Ignore errors when adding event listener
        }
      }

      // React to platform language changes and update selected voice accordingly
      try {
        i18n.on("languageChanged", async (lng: string) => {
          try {
            const currentVoices = get().voices;
            // If voices are not yet populated, fetch them
            const voicesList =
              currentVoices && currentVoices.length > 0 ? currentVoices : await getVoices();
            const appLang = (lng || "en-GB").toLowerCase();
            const base = appLang.split("-")[0];
            const match =
              voicesList.find((v) => (v.lang || "").toLowerCase() === appLang) ||
              voicesList.find((v) => (v.lang || "").toLowerCase().startsWith(base)) ||
              null;

            if (match) {
              localStorage.setItem("selectedVoice", match.name);
              set(() => ({ selectedVoice: match.name, voices: voicesList }));
            }
          } catch (err) {
            logger.info("Failed to update voice on language change", { error: String(err) });
          }
        });
      } catch {
        // Ignore errors when adding i18n listener
      }
    } catch (error) {
      logger.error("Error initializing speech", error);
      // Don't update state if initialization fails, keep default values
    }
  })();

  const handleSetSelectedVoice = (voice: string) => {
    localStorage.setItem("selectedVoice", voice);
    set(() => ({ selectedVoice: voice }));
  };

  const speak = async (text: string, messageId: string) => {
    let { selectedVoice, rate, pitch, voices } = get();

    if (!text) {
      set(() => ({ speakingMessageId: null }));

      return;
    }
    // If "No Voice" is selected, try to pick a better default or refresh once
    if (selectedVoice === "No Voice") {
      if (voices.length > 1) {
        handleSetSelectedVoice(voices[1].name);
        selectedVoice = get().selectedVoice;
      } else {
        try {
          await initEasySpeech();
          const refreshed = await getVoices();

          set(() => ({ voices: refreshed }));
          if (refreshed.length > 1) {
            handleSetSelectedVoice(refreshed[1].name);
            selectedVoice = get().selectedVoice;
          }
        } catch {
          // Ignore errors when adding event listener
        }

        if (get().selectedVoice === "No Voice") {
          logger.warn("No voices available for speech synthesis");
          set(() => ({ speakingMessageId: null }));

          return;
        }
      }
    }

    // Cancel any ongoing speech
    await EasySpeech.cancel();

    try {
      // Set speaking state to true with message ID
      set(() => ({ speakingMessageId: messageId }));

      // Find the voice object
      const availableVoices = await EasySpeech.voices();
      let voice = availableVoices.find((v) => v.name === selectedVoice);

      // If the selected voice is not found, try to use any available voice
      if (!voice && availableVoices.length > 0) {
        logger.warn(
          `Selected voice "${selectedVoice}" not found. Using first available voice instead.`,
        );
        voice = availableVoices[0];
        // Update the selected voice for future use
        handleSetSelectedVoice(voice.name);
      }

      if (!voice) {
        logger.warn(`No voices available for speech synthesis. Selected voice: ${selectedVoice}`);
        set(() => ({ speakingMessageId: null }));

        return;
      }

      // Split text into chunks of approximately 4000 bytes to stay under the 4096 limit
      const MAX_CHUNK_SIZE = 4000;
      const textChunks: string[] = [];
      let currentChunk = "";

      // Split by sentences to maintain natural breaks
      const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];

      for (const sentence of sentences) {
        if ((currentChunk + sentence).length > MAX_CHUNK_SIZE) {
          if (currentChunk) {
            textChunks.push(currentChunk);
            currentChunk = sentence;
          } else {
            // If a single sentence is too long, split it by words
            const words = sentence.split(" ");

            for (const word of words) {
              if ((currentChunk + " " + word).length > MAX_CHUNK_SIZE) {
                textChunks.push(currentChunk);
                currentChunk = word;
              } else {
                currentChunk += (currentChunk ? " " : "") + word;
              }
            }
          }
        } else {
          currentChunk += (currentChunk ? " " : "") + sentence;
        }
      }

      if (currentChunk) {
        textChunks.push(currentChunk);
      }

      textChunks.forEach((chunk, index) => {
        logger.info(`Chunk ${index + 1} length: ${chunk.length} bytes`);
      });

      // Speak each chunk sequentially
      for (const chunk of textChunks) {
        try {
          await EasySpeech.speak({
            text: chunk,
            voice,
            rate,
            pitch,
            start: () => {
              logger.info("Speech started for chunk");
              set(() => ({ speakingMessageId: messageId }));
            },
            end: () => {
              logger.info("Speech ended for chunk");
              // Only set speakingMessageId to null after the last chunk
              if (chunk === textChunks[textChunks.length - 1]) {
                set(() => ({ speakingMessageId: null }));
              }
            },
            error: (error: SpeechSynthesisErrorEvent) => {
              // Handle interruption and cancellation as normal events, not errors
              if (error.error === "interrupted" || error.error === "canceled") {
                // Log interruption/cancellation as info instead of error
                logger.info(`Speech ${error.error}`, {
                  elapsedTime: String(error.elapsedTime),
                });
              } else {
                // Only log actual errors
                logger.error("Speech error", error, {
                  chunkLength: String(chunk.length),
                });
              }
              set(() => ({ speakingMessageId: null }));
            },
          });
        } catch (chunkError) {
          // Check if we're still speaking this message
          const currentSpeakingId = get().speakingMessageId;

          if (currentSpeakingId !== messageId) {
            logger.info("Speech was stopped, ignoring chunk error");

            // Don't re-throw if speech was stopped
            return;
          }

          // Check if it's a cancellation-related error
          if (
            chunkError instanceof SpeechSynthesisErrorEvent &&
            (chunkError.error === "interrupted" || chunkError.error === "canceled")
          ) {
            // Log cancellation as info, not as an error
            logger.info(`Speech ${chunkError.error} during chunk processing`, {
              elapsedTime: String(chunkError.elapsedTime),
              chunkLength: String(chunk.length),
            });
            set(() => ({ speakingMessageId: null }));

            return; // Don't re-throw cancellation errors
          }

          // For other errors, log them but don't throw if it's a cancellation
          if (
            chunkError &&
            typeof chunkError === "object" &&
            "error" in chunkError &&
            chunkError.error === "canceled"
          ) {
            logger.info("Speech canceled during chunk processing", {
              chunkLength: String(chunk.length),
            });
            set(() => ({ speakingMessageId: null }));

            return; // Don't re-throw cancellation errors
          }

          // For all other errors, log and re-throw
          logger.error("Error speaking chunk", chunkError, {
            chunkLength: String(chunk.length),
          });

          throw chunkError; // Re-throw to be caught by outer try-catch
        }
      }
    } catch (error) {
      // Improved error logging with better type handling
      if (error instanceof SpeechSynthesisErrorEvent) {
        // Don't treat interruptions or cancellations as errors
        if (error.error === "interrupted" || error.error === "canceled") {
          logger.info(`Speech ${error.error}`, {
            elapsedTime: String(error.elapsedTime),
          });
          set(() => ({ speakingMessageId: null }));

          return;
        }
      } else if (error instanceof Error) {
        // Check if the error message contains "canceled" or "cancelled"
        const errorMsg = error.message.toLowerCase();

        if (errorMsg.includes("cancel")) {
          logger.info("Speech canceled (from Error object)", {
            message: error.message,
          });
          set(() => ({ speakingMessageId: null }));

          return;
        }
      } else if (error && typeof error === "object") {
        // Check for objects with an error property set to "canceled"
        if ("error" in error && error.error === "canceled") {
          logger.info("Speech canceled (from error object)", {
            error: String(error),
          });
          set(() => ({ speakingMessageId: null }));

          return;
        }
      }

      // Only log as an error if we haven't returned early for cancellation
      logger.error("Error speaking", error, {
        selectedVoice,
        textLength: String(text.length),
        voicesAvailable: String(voices.length),
      });
      set(() => ({ speakingMessageId: null }));
    }
  };

  const stopSpeech = async () => {
    // Always reset the speaking state first to prevent race conditions
    // This ensures that any ongoing or future speech operations will know
    // that speech has been stopped
    set(() => ({ speakingMessageId: null }));

    try {
      // Use a timeout to prevent hanging on mobile devices
      const cancelPromise = EasySpeech.cancel();

      // Create a timeout promise that resolves after 1 second
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          logger.info("Speech cancellation timeout reached");
          resolve(null);
        }, 1000);
      });

      // Race the cancel operation against the timeout
      // This prevents the operation from hanging indefinitely on mobile
      await Promise.race([cancelPromise, timeoutPromise]);

      logger.info("Speech successfully stopped");
    } catch (error) {
      // Enhanced error handling for different error types
      if (error instanceof SpeechSynthesisErrorEvent) {
        // Handle specific speech synthesis errors
        if (error.error === "interrupted" || error.error === "canceled") {
          logger.info(`Speech ${error.error} - this is normal during stopping`);
        } else {
          logger.warn(`Speech synthesis error during stop: ${error.error}`, {
            elapsedTime: String(error.elapsedTime),
            name: error.name,
          });
        }
      } else if (error instanceof Error) {
        // Handle standard JS errors
        logger.error("Error stopping speech", error);
      } else {
        // Handle unknown error types
        logger.error("Unknown error stopping speech", error);
      }

      // No need to reset speaking state here as we already did it at the beginning
    } finally {
      // Additional cleanup for mobile devices
      try {
        // Some mobile browsers might need an additional cancel call
        // or might have lingering speech synthesis objects
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      } catch (cleanupError) {
        logger.info("Additional cleanup error (can be ignored)", { error: String(cleanupError) });
      }
    }
  };

  return {
    ...initialState,
    setSelectedVoice: handleSetSelectedVoice,
    speak,
    stopSpeech,
  };
});

export { useSpeechStore };
